import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, MoreHorizontal } from "lucide-react";
import Button from "../ui/Button";
import { useTheme } from "../../context/ThemeContext";
import {
  getCardBackground,
  getPrimaryText,
  getLightBorder,
} from "../../utils/themeUtils";

export interface ActionMenuItem {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  danger?: boolean;
}

interface ActionMenuProps {
  items: ActionMenuItem[];
  /** Text-label trigger (default). Ignored when iconOnly. */
  label?: string;
  /** Render an ellipsis (⋯) icon trigger instead of a labeled button. */
  iconOnly?: boolean;
  align?: "left" | "right";
  size?: "xs" | "sm" | "md" | "lg";
  variant?: "primary" | "secondary" | "danger" | "ghost";
  /** Accessible name for the icon-only trigger. */
  ariaLabel?: string;
  className?: string;
}

/**
 * Small overflow / actions dropdown. Groups occasional secondary actions
 * behind one trigger so they stop crowding a header or form. Click-outside
 * and Escape close it. Trigger + items use the shared Button styling.
 */
const ActionMenu: React.FC<ActionMenuProps> = ({
  items,
  label,
  iconOnly = false,
  align = "right",
  size = "sm",
  variant = "secondary",
  ariaLabel = "Options",
  className = "",
}) => {
  const { isDarkMode } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Keyboard: opening the menu moves focus to its first item; Up/Down/Home/End
  // move between items. Escape (below) closes and hands focus back to the
  // trigger so a keyboard user is not left on a control that no longer exists.
  const enabledItems = () =>
    Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])') || []
    );
  useEffect(() => {
    if (open) enabledItems()[0]?.focus();
  }, [open]);
  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    const list = enabledItems();
    if (!list.length) return;
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    let next = -1;
    if (e.key === "ArrowDown") next = at < 0 ? 0 : (at + 1) % list.length;
    else if (e.key === "ArrowUp") next = at <= 0 ? list.length - 1 : at - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = list.length - 1;
    if (next < 0) return;
    e.preventDefault();
    list[next].focus();
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        ref.current?.querySelector<HTMLButtonElement>("button[aria-haspopup]")?.focus();
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  if (!items.length) return null;

  return (
    <div className={`relative inline-block ${className}`} ref={ref}>
      <Button
        variant={variant}
        size={size}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={iconOnly ? ariaLabel : undefined}
      >
        {iconOnly ? (
          <MoreHorizontal className="w-4 h-4" />
        ) : (
          <span className="inline-flex items-center gap-1">
            {label}
            <ChevronDown className="w-4 h-4" />
          </span>
        )}
      </Button>

      {open && (
        <div
          role="menu"
          ref={menuRef}
          onKeyDown={onMenuKeyDown}
          className={`absolute z-50 mt-1 min-w-[12rem] rounded-md border shadow-lg py-1 ${getLightBorder(
            isDarkMode
          )} ${getCardBackground(isDarkMode)} ${align === "right" ? "right-0" : "left-0"}`}
        >
          {items.map((item, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5 dark:hover:bg-white/10 ${
                item.danger ? "text-red-600 dark:text-red-400" : getPrimaryText(isDarkMode)
              }`}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActionMenu;
